<template>
  <div class="p-4 md:p-6">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
      <div>
        <h1 class="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
          <Icon icon="mdi:fingerprint" class="text-blue-500" width="32" height="32" />
          Gestión de Huellas Digitales
        </h1>
        <p class="text-sm md:text-base text-gray-600 dark:text-gray-400">
          Registrar y administrar huellas dactilares de empleados para el control de asistencia
        </p>
      </div>
      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700">
          <span class="w-2.5 h-2.5 rounded-full" :class="estadoLector ? 'bg-green-500 animate-pulse' : 'bg-red-500'"></span>
          <span class="text-sm font-medium text-gray-700 dark:text-gray-300">
            {{ estadoLector ? 'Conectado' : 'Desconectado' }}
          </span>
        </div>
        <button
          @click="toggleConexion"
          :disabled="conectando"
          class="px-4 py-2.5 rounded-lg text-white transition-colors text-sm"
          :class="estadoLector ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'"
        >
          <Icon :icon="estadoLector ? 'mdi:power-off' : 'mdi:power-on'" class="mr-2" />
          {{ conectando ? 'Conectando...' : estadoLector ? 'Desconectar' : 'Conectar' }}
        </button>
      </div>
    </div>

    <div v-if="estadoLector" class="mb-4 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div class="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
        <span class="text-sm font-medium text-green-700 dark:text-green-300">
          ✅ Lector listo
        </span>
      </div>
      <span class="text-xs text-green-600 dark:text-green-400 font-mono">USB</span>
    </div>

    <!-- Grid principal -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <!-- Panel de registro -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 border border-gray-200 dark:border-gray-700">
          <h3 class="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center">
            <Icon icon="mdi:account-plus" class="mr-2 text-blue-500" />
            Registrar Huella
          </h3>

          <div class="space-y-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              <Icon icon="mdi:information" class="inline mr-1" />
              Selecciona un empleado y haz clic en "Preparar Captura"
            </p>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Empleado <span class="text-red-500">*</span>
              </label>
              <select
                v-model="usuarioSeleccionado"
                @change="cargarInfoUsuario"
                :disabled="capturaActiva || cargando"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-50"
              >
                <option value="">Seleccionar empleado...</option>
                <option v-for="usuario in usuariosSinHuella" :key="usuario.idUsuario" :value="usuario.idUsuario">
                  {{ usuario.nombreCompleto }} (@{{ usuario.nombreUsuario }})
                </option>
              </select>
              <p class="text-xs text-gray-500 mt-1">{{ usuariosSinHuella.length }} empleados sin huella registrada</p>
            </div>

            <div v-if="usuarioSeleccionado && usuarioInfo" class="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
              <div class="flex items-center gap-3">
                <img
                  :src="getImageUrl(usuarioInfo.fotoPerfil) || defaultAvatar"
                  class="w-12 h-12 rounded-full object-cover border-2 border-blue-300"
                  @error="handleImageError"
                />
                <div>
                  <p class="font-medium text-gray-800 dark:text-white">{{ usuarioInfo.nombreCompleto }}</p>
                  <p class="text-sm text-gray-500">{{ usuarioInfo.rol }}</p>
                  <p class="text-xs text-gray-400">@{{ usuarioInfo.nombreUsuario }}</p>
                </div>
              </div>
              <div v-if="usuarioInfo.tieneHuella" class="mt-2 flex items-center text-green-600 text-sm">
                <Icon icon="mdi:check-circle" class="mr-1" />
                Ya tiene huella registrada
              </div>
            </div>

            <!-- Botón preparar captura / cancelar -->
            <button
              v-if="!capturaActiva"
              @click="activarModoRegistro"
              :disabled="!usuarioSeleccionado || !estadoLector || cargando"
              class="w-full px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center justify-center"
            >
              <Icon icon="mdi:fingerprint" class="mr-2" />
              Preparar Captura
            </button>
            <button
              v-else
              @click="cancelarCaptura"
              class="w-full px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-all text-sm flex items-center justify-center"
            >
              <Icon icon="mdi:close" class="mr-2" />
              Cancelar
            </button>

            <button
              v-if="usuarioInfo?.tieneHuella && !capturaActiva"
              @click="eliminarHuellaUsuario"
              class="w-full px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm flex items-center justify-center"
            >
              <Icon icon="mdi:delete" class="mr-2" />
              Eliminar Huella
            </button>

            <div v-if="!estadoLector" class="text-center text-amber-600 text-sm p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
              <Icon icon="mdi:alert" class="mr-1" />
              Conecta el lector para registrar huellas
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de usuarios con huella -->
      <div class="lg:col-span-2">
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 class="text-lg font-semibold text-gray-800 dark:text-white">Empleados con Huella Registrada</h3>
            <span class="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
              {{ usuariosConHuella.length }} registrados
            </span>
          </div>

          <div v-if="cargando" class="py-12 text-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p class="text-gray-500 mt-2 text-sm">Cargando usuarios...</p>
          </div>

          <div v-else-if="usuariosConHuella.length === 0" class="py-12 text-center text-gray-400">
            <Icon icon="mdi:fingerprint-off" class="text-5xl mx-auto" />
            <p class="mt-2">No hay empleados con huella registrada</p>
            <p class="text-sm">Registra la primera huella usando el panel lateral</p>
          </div>

          <div v-else class="divide-y divide-gray-100 dark:divide-gray-700">
            <div v-for="usuario in usuariosConHuella" :key="usuario.idUsuario"
                 class="px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors flex items-center justify-between">
              <div class="flex items-center gap-3">
                <img 
                  :src="getImageUrl(usuario.fotoPerfil) || defaultAvatar" 
                  class="w-10 h-10 rounded-full object-cover" 
                  @error="handleImageError"
                />
                <div>
                  <p class="font-medium text-gray-800 dark:text-white">{{ usuario.nombreCompleto }}</p>
                  <p class="text-sm text-gray-500">{{ usuario.rol }}</p>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <span class="text-xs text-gray-400">
                  <Icon icon="mdi:calendar" class="mr-1" />
                  {{ formatDate(usuario.fechaRegistroHuella) }}
                </span>
                <button @click="seleccionarUsuario(usuario.idUsuario)" class="px-3 py-1 text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 transition-colors">
                  Ver
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ========== MODAL DE CAPTURA ========== -->
    <Teleport to="body">
      <div
        v-if="capturaActiva"
        class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4"
      >
        <div class="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-8 shadow-2xl text-center">

          <!-- Fase 1: esperando que coloquen el dedo -->
          <template v-if="faseCaptura === 'esperando'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 bg-blue-500/20 rounded-full animate-ping"></div>
              <div class="relative bg-blue-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:fingerprint" class="text-white text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Coloca tu dedo en el lector</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Apoya el dedo con firmeza, sin moverlo
            </p>
            <p v-if="usuarioSeleccionadoNombre" class="text-xs text-blue-500 mt-3 font-medium">
              Registrando para: {{ usuarioSeleccionadoNombre }}
            </p>
          </template>

          <!-- Fase 2: dedo detectado, verificando/procesando -->
          <template v-else-if="faseCaptura === 'procesando'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 rounded-full border-4 border-amber-200 dark:border-amber-900"></div>
              <div class="absolute inset-0 rounded-full border-4 border-amber-500 border-t-transparent animate-spin"></div>
              <div class="relative w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:fingerprint" class="text-amber-500 text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">Verificando huella...</h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">No retires el dedo todavía</p>
          </template>

          <!-- Fase 3: éxito -->
          <template v-else-if="faseCaptura === 'exito'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
              <div class="relative bg-green-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:check-bold" class="text-white text-6xl" />
              </div>
            </div>
            <div v-if="resultadoCaptura?.usuario" class="flex flex-col items-center">
              <img
                :src="getImageUrl(resultadoCaptura.usuario.fotoPerfil) || defaultAvatar"
                class="w-16 h-16 rounded-full object-cover border-4 border-green-200 mb-2"
                @error="handleImageError"
              />
              <h3 class="text-xl font-bold text-gray-800 dark:text-white">{{ resultadoCaptura.usuario.nombreCompleto }}</h3>
              <p class="text-gray-500 dark:text-gray-400 text-sm">{{ resultadoCaptura.usuario.rol }}</p>
              <div
                v-if="resultadoCaptura.tipo && resultadoCaptura.tipo !== 'registro'"
                class="mt-3 px-6 py-2 rounded-full text-lg font-bold"
                :class="resultadoCaptura.tipo === 'entrada' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'"
              >
                {{ resultadoCaptura.tipo.toUpperCase() }} {{ resultadoCaptura.hora }}
              </div>
            </div>
            <p class="text-sm text-green-600 dark:text-green-400 mt-3 font-medium">{{ resultadoCaptura?.mensaje }}</p>
          </template>

          <!-- Fase 4: error / no reconocida -->
          <template v-else-if="faseCaptura === 'error'">
            <div class="relative mx-auto w-28 h-28 mb-5">
              <div class="relative bg-red-500 rounded-full w-28 h-28 flex items-center justify-center">
                <Icon icon="mdi:close-thick" class="text-white text-6xl" />
              </div>
            </div>
            <h3 class="text-xl font-bold text-gray-800 dark:text-white">No se pudo procesar</h3>
            <p class="text-sm text-red-500 mt-1">{{ mensajeError }}</p>
            <button
              @click="reintentarCaptura"
              class="mt-4 px-5 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm"
            >
              Intentar de nuevo
            </button>
          </template>

          <button
            v-if="faseCaptura !== 'procesando'"
            @click="cancelarCaptura"
            class="mt-6 text-sm text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Teleport>

    <!-- ========== TOAST ========== -->
    <div
      v-if="toast.show"
      class="fixed bottom-4 right-4 max-w-sm w-full rounded-lg shadow-lg p-4 transition-all duration-300 z-[100]"
      :class="toast.type === 'success' ? 'bg-green-500' : toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500'"
    >
      <div class="flex items-start gap-3">
        <Icon :icon="toast.type === 'success' ? 'mdi:check-circle' : toast.type === 'error' ? 'mdi:alert-circle' : 'mdi:information'" class="text-white text-xl flex-shrink-0" />
        <div>
          <p class="text-white font-medium">{{ toast.title }}</p>
          <p class="text-white/80 text-sm">{{ toast.message }}</p>
        </div>
        <button @click="toast.show = false" class="text-white/60 hover:text-white ml-auto">
          <Icon icon="mdi:close" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import dayjs from 'dayjs';
import * as signalR from '@microsoft/signalr';
import {
  conectarLector,
  desconectarLector,
  obtenerEstadoLector,
  obtenerUsuariosConHuella,
  eliminarHuella,
  activarModoRegistro
} from '@/api/huella.js';
import { obtenerUsuarios, obtenerUrlImagen } from '@/api/usuarios.js';
import { getCurrentApiUrl } from '@/api/index.js';

const API_BASE_URL = (typeof process !== 'undefined' && process.env && process.env.VUE_APP_API_BASE_URL)
  || getCurrentApiUrl();
const HUB_URL = `${API_BASE_URL}/asistenciaHub`;
const TIMEOUT_ESPERA_MS = 20000;

export default {
  name: 'GestionHuellas',
  components: { Icon },
  data() {
    return {
      cargando: false,
      conectando: false,
      estadoLector: false,
      usuariosConHuella: [],
      usuariosSinHuella: [],
      usuarioSeleccionado: null,
      usuarioInfo: null,
      defaultAvatar: 'https://ui-avatars.com/api/?name=Usuario&background=4F46E5&color=fff&size=100',
      capturaActiva: false,
      faseCaptura: 'esperando',
      resultadoCaptura: null,
      mensajeError: '',
      timeoutEsperaId: null,
      hubConnection: null,
      toast: {
        show: false,
        type: 'success',
        title: '',
        message: ''
      }
    };
  },
  computed: {
    usuarioSeleccionadoNombre() {
      return this.usuarioInfo?.nombreCompleto || '';
    }
  },
  async mounted() {
    await this.init();
    await this.conectarSignalR();
  },
  beforeUnmount() {
    this.limpiarTimeoutEspera();
    if (this.hubConnection) {
      this.hubConnection.stop();
    }
  },
  methods: {
    // ========== INICIALIZACIÓN ==========
    async init() {
      await this.cargarEstadoLector();
      await this.cargarUsuariosConHuella();
      await this.cargarUsuariosSinHuella();
    },

    // ========== SIGNALR ==========
    async conectarSignalR() {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      
      console.log('🔗 Conectando a SignalR en:', HUB_URL);
      console.log('🔑 Token presente:', !!token);

      this.hubConnection = new signalR.HubConnectionBuilder()
        .withUrl(HUB_URL, {
          accessTokenFactory: () => token || '',
          transport: signalR.HttpTransportType.WebSocket | signalR.HttpTransportType.LongPolling
        })
        .withAutomaticReconnect()
        .build();

      this.hubConnection.on('Conectado', (data) => {
        console.log('✅ Conectado al Hub:', data);
      });

      this.hubConnection.on('HuellaProcesando', (data) => {
        console.log('📢 HuellaProcesando recibido:', data);
        if (this.capturaActiva) {
          this.limpiarTimeoutEspera();
          this.faseCaptura = 'procesando';
        }
      });

      this.hubConnection.on('HuellaDetectada', (data) => {
        console.log('📢 HuellaDetectada recibido:', data);
        this.manejarResultadoHuella(data);
      });

      this.hubConnection.on('HuellaError', (data) => {
        console.log('📢 HuellaError recibido:', data);
        if (this.capturaActiva) {
          this.limpiarTimeoutEspera();
          this.faseCaptura = 'error';
          this.mensajeError = data?.mensaje || 'Ocurrió un error al leer la huella';
        }
        this.mostrarToast('error', 'Error del lector', data?.mensaje || 'Error desconocido');
      });

      this.hubConnection.on('EstadoLector', (data) => {
        console.log('📢 EstadoLector recibido:', data);
        this.estadoLector = !!data?.conectado;
      });

      this.hubConnection.on('ModoRegistroActivado', (data) => {
        console.log('📢 ModoRegistroActivado:', data);
      });

      this.hubConnection.on('ActualizarAsistencia', () => {
        console.log('📢 ActualizarAsistencia recibido');
      });

      try {
        await this.hubConnection.start();
        console.log('✅ SignalR conectado exitosamente');
        await this.hubConnection.invoke('RequestEstado');
      } catch (error) {
        console.error('❌ Error conectando SignalR:', error);
        this.mostrarToast('error', 'Conexión en tiempo real', 'No se pudo conectar al servidor de eventos');
      }
    },

    manejarResultadoHuella(data) {
      console.log('🔄 Manejar resultado huella:', data);
      this.limpiarTimeoutEspera();

      if (!this.capturaActiva) {
        if (data?.exito) {
          this.mostrarToast('success', 'Registro exitoso', data.mensaje);
        }
        return;
      }

      if (data?.exito) {
        this.faseCaptura = 'exito';
        this.resultadoCaptura = {
          usuario: {
            nombreCompleto: data.nombreCompleto || 'Usuario',
            rol: data.rol || 'Sin rol',
            cedula: data.cedula || '',
            fotoPerfil: data.fotoPerfil
          },
          tipo: data.tipoRegistro,
          hora: this.formatTime(data.fechaHora),
          mensaje: data.mensaje
        };

        if (data.tipoRegistro === 'registro') {
          this.cargarUsuariosConHuella();
          this.cargarUsuariosSinHuella();
          this.usuarioInfo = null;
          this.usuarioSeleccionado = null;
        }

        setTimeout(() => {
          if (this.faseCaptura === 'exito') this.capturaActiva = false;
        }, 2500);
      } else {
        this.faseCaptura = 'error';
        this.mensajeError = data?.mensaje || 'Huella no reconocida';
      }
    },

    // ========== CARGA DE DATOS ==========
    async cargarEstadoLector() {
      try {
        const response = await obtenerEstadoLector();
        if (response.success && response.data) {
          this.estadoLector = response.data.conectado || false;
        }
      } catch (error) {
        console.error('Error cargando estado:', error);
        this.estadoLector = false;
      }
    },

    async cargarUsuariosConHuella() {
      this.cargando = true;
      try {
        console.log('🔄 Cargando usuarios con huella...');
        const response = await obtenerUsuariosConHuella();
        console.log('✅ DATOS CRUDOS de usuarios con huella:', response);
        
        if (response.success && response.data) {
          // Mapear los datos para asegurar que todos los campos estén disponibles
          this.usuariosConHuella = response.data.map(user => ({
            ...user,
            nombreCompleto: user.nombreCompleto || `${user.nombre || ''} ${user.apellido || ''}`.trim() || 'Usuario',
            rol: user.rol || 'Sin rol',
            nombreUsuario: user.nombreUsuario || user.email || 'sin usuario'
          }));
          console.log('✅ Usuarios con huella mapeados:', this.usuariosConHuella);
        } else {
          this.usuariosConHuella = [];
        }
      } catch (error) {
        console.error('❌ Error cargando usuarios con huella:', error);
        this.usuariosConHuella = [];
      } finally {
        this.cargando = false;
        console.log('✅ Carga de usuarios con huella finalizada');
      }
    },

    async cargarUsuariosSinHuella() {
      try {
        console.log('🔄 Cargando usuarios sin huella...');
        const response = await obtenerUsuarios('activos');
        console.log('✅ DATOS CRUDOS de usuarios activos:', response);
        
        if (response.success && response.data) {
          const idsConHuella = this.usuariosConHuella.map(u => u.idUsuario);
          this.usuariosSinHuella = response.data
            .filter(u => !idsConHuella.includes(u.idUsuario))
            .map(user => ({
              ...user,
              nombreCompleto: user.nombreCompleto || `${user.nombre || ''} ${user.apellido || ''}`.trim() || 'Usuario',
              rol: user.rol || 'Sin rol',
              nombreUsuario: user.nombreUsuario || user.email || 'sin usuario'
            }));
          console.log('✅ Usuarios sin huella mapeados:', this.usuariosSinHuella);
        } else {
          this.usuariosSinHuella = [];
        }
      } catch (error) {
        console.error('❌ Error cargando usuarios sin huella:', error);
        this.usuariosSinHuella = [];
      }
    },

    // ========== CONEXIÓN DEL LECTOR ==========
    async toggleConexion() {
      this.conectando = true;
      try {
        if (this.estadoLector) {
          const response = await desconectarLector();
          if (response.success) {
            this.estadoLector = false;
            this.mostrarToast('success', 'Desconectado', 'Lector desconectado');
          }
        } else {
          const response = await conectarLector();
          if (response.success && response.data?.exito !== false) {
            this.estadoLector = true;
            this.mostrarToast('success', 'Conectado', 'Lector USB conectado');
          } else {
            this.mostrarToast('error', 'Error', response.data?.mensaje || 'No se pudo conectar');
          }
        }
      } catch (error) {
        console.error('Error en toggleConexion:', error);
        this.mostrarToast('error', 'Error', error.message || 'Error al conectar/desconectar');
      } finally {
        this.conectando = false;
      }
    },

    // ========== GESTIÓN DE USUARIOS ==========
    async cargarInfoUsuario() {
      if (!this.usuarioSeleccionado) {
        this.usuarioInfo = null;
        return;
      }
      
      // Buscar en ambas listas
      const usuario = this.usuariosConHuella.find(u => u.idUsuario === this.usuarioSeleccionado);
      if (usuario) {
        this.usuarioInfo = usuario;
      } else {
        const sinHuella = this.usuariosSinHuella.find(u => u.idUsuario === this.usuarioSeleccionado);
        if (sinHuella) {
          this.usuarioInfo = sinHuella;
        } else {
          // Si no se encuentra, buscar en la lista completa
          try {
            const response = await obtenerUsuarios('activos');
            if (response.success && response.data) {
              const encontrado = response.data.find(u => u.idUsuario === this.usuarioSeleccionado);
              if (encontrado) {
                this.usuarioInfo = {
                  ...encontrado,
                  nombreCompleto: encontrado.nombreCompleto || `${encontrado.nombre || ''} ${encontrado.apellido || ''}`.trim() || 'Usuario',
                  rol: encontrado.rol || 'Sin rol',
                  nombreUsuario: encontrado.nombreUsuario || encontrado.email || 'sin usuario'
                };
                return;
              }
            }
          } catch (error) {
            console.error('Error buscando usuario:', error);
          }
          this.usuarioInfo = null;
        }
      }
    },

    async seleccionarUsuario(id) {
      this.usuarioSeleccionado = id;
      await this.cargarInfoUsuario();
    },

    // ========== CAPTURA ==========
    async activarModoRegistro() {
      if (!this.usuarioSeleccionado || !this.estadoLector) {
        this.mostrarToast('error', 'Error', 'Selecciona un usuario y conecta el lector');
        return;
      }

      this.capturaActiva = true;
      this.faseCaptura = 'esperando';
      this.resultadoCaptura = null;
      this.mensajeError = '';

      try {
        const response = await activarModoRegistro(this.usuarioSeleccionado);
        console.log('Respuesta activarModoRegistro:', response);
        
        if (!response.success || !response.data?.exito) {
          this.faseCaptura = 'error';
          this.mensajeError = response.data?.mensaje || 'No se pudo activar el modo de registro';
          return;
        }
      } catch (error) {
        console.error('Error al activar modo registro:', error);
        this.faseCaptura = 'error';
        this.mensajeError = error.message || 'No se pudo activar el modo de registro';
        return;
      }

      this.timeoutEsperaId = setTimeout(() => {
        if (this.faseCaptura === 'esperando') {
          this.mostrarToast('info', 'Sigue esperando', 'Aún no se detecta ningún dedo en el lector');
        }
      }, TIMEOUT_ESPERA_MS);
    },

    cancelarCaptura() {
      this.limpiarTimeoutEspera();
      this.capturaActiva = false;
      this.faseCaptura = 'esperando';
      this.resultadoCaptura = null;
    },

    reintentarCaptura() {
      this.faseCaptura = 'esperando';
      this.mensajeError = '';
      this.timeoutEsperaId = setTimeout(() => {
        if (this.faseCaptura === 'esperando') {
          this.mostrarToast('info', 'Sigue esperando', 'Aún no se detecta ningún dedo en el lector');
        }
      }, TIMEOUT_ESPERA_MS);
    },

    limpiarTimeoutEspera() {
      if (this.timeoutEsperaId) {
        clearTimeout(this.timeoutEsperaId);
        this.timeoutEsperaId = null;
      }
    },

    // ========== GESTIÓN DE HUELLAS ==========
    async eliminarHuellaUsuario() {
      if (!this.usuarioSeleccionado) return;
      if (!confirm(`¿Estás seguro de eliminar la huella de ${this.usuarioInfo?.nombreCompleto || 'este usuario'}?`)) return;

      try {
        const response = await eliminarHuella(this.usuarioSeleccionado);
        if (response.success && response.data?.exito) {
          this.mostrarToast('success', 'Huella eliminada', 'Huella eliminada correctamente');
          await this.cargarUsuariosConHuella();
          await this.cargarUsuariosSinHuella();
          this.usuarioInfo = null;
          this.usuarioSeleccionado = null;
        } else {
          this.mostrarToast('error', 'Error', response.data?.mensaje || 'Error al eliminar la huella');
        }
      } catch (error) {
        console.error('Error eliminando huella:', error);
        this.mostrarToast('error', 'Error', error.message || 'Error al eliminar la huella');
      }
    },

    // ========== UTILIDADES ==========
    getImageUrl(fotoPerfil) {
      if (!fotoPerfil) return null;
      return obtenerUrlImagen(fotoPerfil);
    },
    
    handleImageError(event) {
      event.target.src = this.defaultAvatar;
    },
    
    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    },
    
    formatTime(date) {
      if (!date) return '';
      return dayjs(date).format('HH:mm:ss');
    },
    
    mostrarToast(type, title, message) {
      this.toast.type = type;
      this.toast.title = title;
      this.toast.message = message;
      this.toast.show = true;
      setTimeout(() => { this.toast.show = false; }, 4000);
    }
  }
};
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
@keyframes ping {
  0% { transform: scale(1); opacity: 1; }
  75%, 100% { transform: scale(1.5); opacity: 0; }
}
.animate-ping {
  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}
</style>